// Today's card priority, on the new store: a question, then an approval,
// then work. Summary uses the same session rule, and the approval strip the
// same landing rule.
import type { Issue, Landing, Proposal, Question, Session } from '../api/types'
import { stuckWhy } from './stuck'

export type Live = { kind: 'quiet' } | { kind: 'needs' } | { kind: 'approve'; proposal: string }
  | { kind: 'working'; session: Session; workers: number } | { kind: 'stuck'; why: string }

/** A turn running, exactly as the board's Working chip counts it. */
export function workingSessions(project: string | null, sessions: ReadonlyMap<string, Session>): Session[] {
  return [...sessions.values()].filter(s => s.project_id === project && s.state === 'running')
}

export function approvalLandings(project: string | null, landings: ReadonlyMap<string, Landing>): Landing[] {
  return [...landings.values()].filter(l => l.project_id === project && (l.state === 'running' || l.state === 'deferred') && l.waiting_for?.for === 'approval')
}

/** A batch approval belongs to every issue it holds, as well as its leading proposal's. */
export function approvalIssueIds(landing: Landing, proposals: ReadonlyMap<string, Proposal>): string[] {
  const waiting = landing.waiting_for
  if (waiting?.for !== 'approval') return []
  return [...waiting.subject.proposals, landing.proposal_id].flatMap(id => {
    const proposal = proposals.get(id)
    return proposal ? [proposal.issue_id] : []
  })
}

/** `questions` is already restricted by needsYou to the signed-in member's open questions. */
export function liveOf(issue: Issue, sessions: ReadonlyMap<string, Session>, questions: readonly Question[],
  landings: ReadonlyMap<string, Landing>, proposals: ReadonlyMap<string, Proposal>, returned: ReadonlyMap<string, Proposal> = new Map()): Live {
  if (questions.some(q => q.issue === issue.id)) return { kind: 'needs' }
  const approval = approvalLandings(issue.project, landings).find(l => approvalIssueIds(l, proposals).includes(issue.id))
  if (approval) return { kind: 'approve', proposal: approval.proposal_id }
  const working = workingSessions(issue.project, sessions).filter(s => s.issue_id === issue.id)
  const session = working.find(s => s.origin.kind !== 'delegated') ?? working[0]
  if (session) return { kind: 'working', session, workers: working.length - 1 }
  const why = stuckWhy(issue, sessions.values(), proposals.values(), returned.values())
  return why ? { kind: 'stuck', why } : { kind: 'quiet' }
}
